import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  BarChart3, Users, Eye, Timer, Target, TrendingUp, RefreshCw, MapPin,
  MonitorSmartphone, Megaphone, Route,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/apiConfig';

const RANGE_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

const PIE_COLORS = ['#7c3aed', '#db2777', '#0891b2', '#f59e0b', '#10b981', '#6366f1', '#ef4444', '#8b5cf6'];

const fmtDuration = (s) => {
  if (!s) return '0s';
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

const Kpi = ({ icon: Icon, label, value, sub, tint, testid }) => (
  <Card className="p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
      <Icon className={`w-4 h-4 ${tint}`} />
    </div>
    <p className="text-2xl font-bold text-slate-900 mt-2">{value}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
  </Card>
);

export default function AnalyticsDashboard() {
  const { token } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [toggling, setToggling] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/dashboard`, {
        params: { days }, headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      setEnabled(res.data.enabled);
    } catch (err) {
      console.error('analytics dashboard error', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleEnabled = async (checked) => {
    setToggling(true);
    setEnabled(checked);
    try {
      await axios.put(`${API}/analytics/config`, { enabled: checked }, { headers });
      toast.success(`Player analytics ${checked ? 'enabled' : 'disabled'}`);
    } catch {
      setEnabled(!checked);
      toast.error('Failed to update setting');
    } finally {
      setToggling(false);
    }
  };

  const k = data?.kpis || {};
  const funnelMax = Math.max(1, ...(data?.funnel || []).map((f) => f.value));

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="analytics-dashboard-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight flex items-center gap-2">
              <BarChart3 className="w-6 h-6 sm:w-7 sm:h-7 text-violet-600" />
              Stats
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Player-facing traffic, sources, behaviour &amp; conversions. Admin pages are never tracked.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid="analytics-toggle-wrap">
              <span className="text-xs font-medium text-slate-600">Tracking</span>
              <Switch
                checked={enabled}
                disabled={toggling}
                onCheckedChange={toggleEnabled}
                data-testid="analytics-enabled-switch"
              />
              <span className={`text-xs font-semibold ${enabled ? 'text-emerald-600' : 'text-slate-400'}`}>
                {enabled ? 'On' : 'Off'}
              </span>
            </div>
            <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
              <SelectTrigger className="w-[150px]" data-testid="analytics-range-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RANGE_OPTIONS.map((r) => (
                  <SelectItem key={r.value} value={String(r.value)}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={fetchData} disabled={loading} variant="outline" data-testid="analytics-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {!enabled && (
          <Card className="p-4 border-amber-200 bg-amber-50/60" data-testid="analytics-disabled-banner">
            <p className="text-sm text-amber-700">
              Player analytics tracking is currently <strong>OFF</strong>. New visits won’t be recorded until you turn it back on.
            </p>
          </Card>
        )}

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <Kpi icon={Users} label="Visits" value={k.visits ?? 0} tint="text-violet-600" testid="kpi-visits" />
          <Kpi icon={Users} label="Unique Visitors" value={k.unique_visitors ?? 0} tint="text-fuchsia-600" testid="kpi-unique" />
          <Kpi icon={Eye} label="Page Views" value={k.pageviews ?? 0} tint="text-cyan-600" testid="kpi-pageviews" />
          <Kpi icon={Timer} label="Avg. Session" value={fmtDuration(k.avg_duration_sec)} tint="text-amber-600" testid="kpi-duration" />
          <Kpi icon={Target} label="Conversions" value={k.conversions ?? 0} tint="text-emerald-600" testid="kpi-conversions" />
          <Kpi icon={TrendingUp} label="Conv. Rate" value={`${k.conversion_rate ?? 0}%`} tint="text-rose-600" testid="kpi-conv-rate" />
        </div>

        {/* Traffic over time */}
        <Card className="p-4" data-testid="analytics-timeseries-card">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Traffic Over Time</h3>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <AreaChart data={data?.timeseries || []} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="gVisits" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0891b2" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#0891b2" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} minTickGap={20} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="visits" stroke="#7c3aed" fill="url(#gVisits)" strokeWidth={2} name="Visits" />
                <Area type="monotone" dataKey="pageviews" stroke="#0891b2" fill="url(#gViews)" strokeWidth={2} name="Page Views" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Sources + Devices */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Card className="p-4" data-testid="analytics-sources-card">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Route className="w-4 h-4 text-violet-500" /> Traffic Sources
            </h3>
            {(data?.sources || []).length === 0 ? (
              <p className="text-xs text-slate-400">No data yet.</p>
            ) : (
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={data.sources} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#7c3aed" radius={[0, 4, 4, 0]} name="Visits" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <Card className="p-4" data-testid="analytics-devices-card">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <MonitorSmartphone className="w-4 h-4 text-cyan-500" /> Devices
            </h3>
            {(data?.devices || []).length === 0 ? (
              <p className="text-xs text-slate-400">No data yet.</p>
            ) : (
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.devices} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                      {data.devices.map((e, i) => (<Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>

        {/* Funnel + Conversions by type */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Card className="p-4" data-testid="analytics-funnel-card">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-emerald-500" /> Conversion Funnel
            </h3>
            <div className="space-y-2">
              {(data?.funnel || []).map((f) => (
                <div key={f.stage} className="flex items-center gap-3">
                  <span className="text-xs text-slate-500 w-24 shrink-0">{f.stage}</span>
                  <div className="flex-1 bg-slate-100 rounded h-6 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded flex items-center justify-end pr-2"
                      style={{ width: `${Math.max((f.value / funnelMax) * 100, f.value ? 6 : 0)}%` }}
                    >
                      <span className="text-[11px] font-semibold text-white">{f.value}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-4" data-testid="analytics-conversions-card">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-rose-500" /> Conversions by Type
            </h3>
            {(data?.conversions_by_type || []).length === 0 ? (
              <p className="text-xs text-slate-400">No conversions in this window.</p>
            ) : (
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={data.conversions_by_type} margin={{ left: -10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#db2777" radius={[4, 4, 0, 0]} name="Count" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>

        {/* Top pages + Locations + Campaigns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <Card className="p-4" data-testid="analytics-toppages-card">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Eye className="w-4 h-4 text-cyan-500" /> Top Pages
            </h3>
            {(data?.top_pages || []).length === 0 ? (
              <p className="text-xs text-slate-400">No data yet.</p>
            ) : (
              <div className="space-y-1.5">
                {data.top_pages.map((p) => (
                  <div key={p.path} className="flex items-center justify-between text-xs">
                    <span className="text-slate-700 font-mono truncate mr-2">{p.path}</span>
                    <span className="shrink-0 font-semibold text-slate-800">{p.views}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-4" data-testid="analytics-locations-card">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-emerald-500" /> Top Locations
            </h3>
            {(data?.locations || []).length === 0 ? (
              <p className="text-xs text-slate-400">No location data yet.</p>
            ) : (
              <div className="space-y-1.5">
                {data.locations.map((l) => (
                  <div key={l.name} className="flex items-center justify-between text-xs">
                    <span className="text-slate-700 truncate mr-2">{l.name}</span>
                    <span className="shrink-0 font-semibold text-slate-800">{l.value}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-4" data-testid="analytics-campaigns-card">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-amber-500" /> Top Campaigns
            </h3>
            {(data?.campaigns || []).length === 0 ? (
              <p className="text-xs text-slate-400">No UTM campaign traffic yet.</p>
            ) : (
              <div className="space-y-1.5">
                {data.campaigns.map((c) => (
                  <div key={c.name} className="flex items-center justify-between text-xs">
                    <span className="text-slate-700 truncate mr-2">{c.name}</span>
                    <span className="shrink-0 font-semibold text-slate-800">{c.value}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
